import { useAuthStore } from '@/stores/auth';
import { TechnicianInstallations } from './TechnicianInstallations';
/**
 * ITBP Installations List — admin "Control Tower" landing (spec §8).
 *
 * Filters by technician / date / status / project / site and lists every
 * installation job with state, site, technician, serial number, and a
 * button to the detail page (photos + map + approve/reject/re-upload).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Search, Filter, MapPin, Loader2, CheckCircle2, AlertTriangle, Clock, RefreshCw, XCircle, Plus, X } from 'lucide-react';

type JobStatus = 'PENDING' | 'IN_PROGRESS' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'PARTIAL';

interface JobRow {
  id: string;
  status: JobStatus;
  serialNumber?: string | null;
  hasPendingWork: boolean;
  submittedAt?: string | null;
  approvedAt?: string | null;
  createdAt: string;
  site?: { id: string; siteName: string; villageName: string; district?: string | null; project?: { id: string; name: string } } | null;
  product?: { id: string; name: string; sku?: string | null } | null;
  technician?: { id: string; firstName: string; lastName: string } | null;
  steps?: Array<{ stepKey: string; status: string }>;
}

interface Summary {
  total: number;
  byStatus: { approved: number; submitted: number; partial: number; pending: number; inProgress: number; rejected: number };
  completionPct: number;
  pendingCount: number;
}

const STATUS_STYLES: Record<JobStatus, string> = {
  PENDING:      'bg-gray-100 text-gray-700',
  IN_PROGRESS:  'bg-blue-50 text-blue-700',
  SUBMITTED:    'bg-amber-50 text-amber-700',
  APPROVED:     'bg-emerald-50 text-emerald-700',
  REJECTED:     'bg-red-50 text-red-700',
  PARTIAL:      'bg-orange-50 text-orange-700',
};

export function ITBPInstallationsPage() { const user = useAuthStore(s=>s.user); return user?.role?.name === 'service_tech' ? <TechnicianInstallations/> : <ITBPInstallationsPageManager/>; }
function ITBPInstallationsPageManager() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // BUG-15 fix: honour ?action=new — open "New Installation Job" modal immediately
  const [newJobOpen, setNewJobOpen] = useState(() => searchParams.get('action') === 'new');

  // When the modal closes, remove ?action=new from the URL so a refresh doesn't re-open it.
  const closeNewJob = () => {
    setNewJobOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  };

  const [rows, setRows] = useState<JobRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'' | JobStatus>('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  async function load() {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      if (search) params.search = search;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const [listRes, summaryRes] = await Promise.all([
        api.get('/installation-jobs', { params }),
        api.get('/installation-jobs/reports/summary'),
      ]);
      setRows(listRes.data?.data ?? []);
      setSummary(summaryRes.data?.data ?? null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, dateFrom, dateTo]);

  const kpis = useMemo(() => summary ? [
    { label: 'Total Jobs',   value: summary.total,                       tone: 'bg-slate-50 text-slate-700' },
    { label: 'Approved',     value: summary.byStatus.approved,           tone: 'bg-emerald-50 text-emerald-700' },
    { label: 'Submitted',    value: summary.byStatus.submitted,          tone: 'bg-amber-50 text-amber-700' },
    { label: 'In Progress',  value: summary.byStatus.inProgress,         tone: 'bg-blue-50 text-blue-700' },
    { label: 'With Pending Work', value: summary.pendingCount,           tone: 'bg-orange-50 text-orange-700' },
    { label: 'Completion',   value: `${summary.completionPct}%`,         tone: 'bg-indigo-50 text-indigo-700' },
  ] : [], [summary]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ITBP Installations</h1>
          <p className="text-sm text-gray-500 mt-1">
            Solar Water Heater installation verifications — Ladakh frontier outposts.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setNewJobOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" /> New Installation
          </button>
          <button
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 text-sm"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* BUG-15: New Installation Job modal */}
      {newJobOpen && (
        <NewInstallationModal
          onClose={closeNewJob}
          onCreated={() => { closeNewJob(); load(); }}
        />
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {kpis.map(k => (
          <div key={k.label} className={`rounded-lg p-3 ${k.tone}`}>
            <div className="text-xs uppercase tracking-wide opacity-70">{k.label}</div>
            <div className="text-2xl font-semibold mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-500 mb-1">Search serial / site / village</label>
          <div className="relative">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') load(); }}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm"
              placeholder="e.g. Hanle, SS-200-0005..."
            />
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value as any)}
            className="border border-gray-200 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="PENDING">Pending</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="APPROVED">Approved</option>
            <option value="PARTIAL">Partial</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border border-gray-200 rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border border-gray-200 rounded-md px-3 py-2 text-sm" />
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700"
        >
          <Filter className="w-4 h-4" /> Apply
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No installation jobs match these filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Product / Serial</th>
                <th className="px-4 py-3">Technician</th>
                <th className="px-4 py-3">Steps</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const captured = (r.steps ?? []).length;
                return (
                  <tr key={r.id} className="border-b last:border-b-0 border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.site?.siteName ?? '—'}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {r.site?.villageName ?? ''} {r.site?.district ? `· ${r.site.district}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.site?.project?.name ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{r.product?.name ?? '—'}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.serialNumber ?? ''}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.technician ? `${r.technician.firstName} ${r.technician.lastName}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <span className="font-medium">{captured}</span> / 9
                        {r.hasPendingWork && <AlertTriangle className="w-3 h-3 text-orange-500" />}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 ${STATUS_STYLES[r.status]}`}>
                        {r.status === 'APPROVED' && <CheckCircle2 className="w-3 h-3" />}
                        {r.status === 'REJECTED' && <XCircle className="w-3 h-3" />}
                        {r.status === 'IN_PROGRESS' && <Clock className="w-3 h-3" />}
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => navigate(`/service/itbp-installations/${r.id}`)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── New Installation Job Modal (BUG-15) ──────────────────────────────────────
interface Site {
  id: string;
  siteName: string;
  villageName: string;
  project?: { id: string; name: string } | null;
}

function NewInstallationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [siteId, setSiteId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSitesLoading(true);
    api.get('/project-sites', { params: { projectType: 'INSTALLATION', limit: 200 } })
      .then(r => setSites(r.data?.data ?? r.data ?? []))
      .catch(() => setSites([]))
      .finally(() => setSitesLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!siteId) { setError('Please select a site.'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post('/installation-jobs', { siteId, serialNumber: serialNumber.trim() || undefined });
      onCreated();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to create installation job.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">New Installation Job</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site <span className="text-red-500">*</span></label>
            {sitesLoading ? (
              <div className="text-sm text-gray-400"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading sites…</div>
            ) : (
              <select
                value={siteId}
                onChange={e => setSiteId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a site…</option>
                {sites.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.siteName} — {s.villageName}{s.project ? ` · ${s.project.name}` : ''}
                  </option>
                ))}
              </select>
            )}
            {sites.length === 0 && !sitesLoading && (
              <p className="text-xs text-gray-500 mt-1">No installation sites found. Create a Project Site first.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number (optional)</label>
            <input
              type="text"
              value={serialNumber}
              onChange={e => setSerialNumber(e.target.value)}
              placeholder="e.g. SS-200-0020"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !siteId}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Job
          </button>
        </div>
      </div>
    </div>
  );
}
